import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./ResetPassword.module.scss";
import { motion as Motion } from "framer-motion";

const appear = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const MIN_PWD = 8;

function ResetForm({ token, t, loading, setLoading, setErr }) {
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);

  const canSubmit = useMemo(() => {
    if (!token) return false;
    if (!pwd || pwd.length < MIN_PWD) return false;
    if (pwd !== pwd2) return false;
    return true;
  }, [token, pwd, pwd2]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!token) {
      setErr(
        t("resetPassword.missingToken", {
          defaultValue: "Reset link is missing or invalid.",
        }),
      );
      return;
    }
    if (!pwd || pwd.length < MIN_PWD) {
      setErr(
        t("resetPassword.min", {
          min: MIN_PWD,
          defaultValue: "Password must be at least {{min}} characters.",
        }),
      );
      return;
    }
    if (pwd !== pwd2) {
      setErr(
        t("resetPassword.mismatch", {
          defaultValue: "Passwords do not match.",
        }),
      );
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.post(
        "/v1/auth/password/reset",
        { token, newPassword: pwd },
        { skipToast: true },
      );
      if (data?.ok) {
        toast.success(
          t("resetPassword.success", {
            defaultValue:
              "Password updated. Please log in with your new password.",
          }),
        );
        // Small delay so users see the toast
        setTimeout(() => {
          window.scrollTo(0, 0);
          // navigate after toast
          location.assign("/login");
        }, 50);
      } else {
        throw new Error("Reset failed");
      }
    } catch (e2) {
      const msg =
        e2?.response?.data?.message ||
        t("resetPassword.failed", {
          defaultValue:
            "Could not reset the password. The link may have expired.",
        });
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className={cls.form} onSubmit={onSubmit} noValidate>
      {/* New password */}
      <div className={cls.group}>
        <label className={cls.label}>
          {t("resetPassword.new", { defaultValue: "New password" })}
        </label>
        <div className={cls.row}>
          <input
            className={cls.input}
            type={showPwd ? "text" : "password"}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder={t("resetPassword.newPlaceholder", {
              defaultValue: "Enter new password",
            })}
            autoComplete="new-password"
            required
          />
          <button
            type="button"
            className={cls.eyeBtn}
            onClick={() => setShowPwd((v) => !v)}
            aria-label={t("resetPassword.showHide", {
              defaultValue: "Show/Hide",
            })}
            title={t("resetPassword.showHide", { defaultValue: "Show/Hide" })}
            aria-pressed={showPwd ? "true" : "false"}
          >
            {showPwd ? "🙈" : "👁️"}
          </button>
        </div>
        <div className={cls.help}>
          {t("resetPassword.min", {
            min: MIN_PWD,
            defaultValue: "At least {{min}} characters.",
          })}
        </div>
      </div>

      {/* Confirm */}
      <div className={cls.group}>
        <label className={cls.label}>
          {t("resetPassword.confirm", { defaultValue: "Confirm password" })}
        </label>
        <div className={cls.row}>
          <input
            className={cls.input}
            type={showPwd2 ? "text" : "password"}
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            placeholder={t("resetPassword.confirmPlaceholder", {
              defaultValue: "Re-enter new password",
            })}
            autoComplete="new-password"
            required
          />
          <button
            type="button"
            className={cls.eyeBtn}
            onClick={() => setShowPwd2((v) => !v)}
            aria-label={t("resetPassword.showHide", {
              defaultValue: "Show/Hide",
            })}
            title={t("resetPassword.showHide", { defaultValue: "Show/Hide" })}
            aria-pressed={showPwd2 ? "true" : "false"}
          >
            {showPwd2 ? "🙈" : "👁️"}
          </button>
        </div>
      </div>

      <button
        className={cls.submit}
        disabled={loading || !canSubmit}
        aria-busy={loading ? "true" : "false"}
      >
        {loading
          ? t("resetPassword.saving", { defaultValue: "Saving…" })
          : t("resetPassword.saveBtn", { defaultValue: "Save new password" })}
      </button>

      <div className={cls.altRow}>
        <span>
          {t("resetPassword.remembered", {
            defaultValue: "Remembered your password?",
          })}
        </span>
        <Link to="/login" className={cls.link}>
          {t("resetPassword.goLogin", { defaultValue: "Go to login" })}
        </Link>
      </div>
    </form>
  );
}

export default function ResetPassword() {
  const t = (k, o) => i18n.t(k, o);
  const [search] = useSearchParams();
  const token = search.get("token");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) {
      setErr(
        t("resetPassword.missingToken", {
          defaultValue: "Reset link is missing or invalid.",
        }),
      );
    }
  }, [token]);

  return (
    <main className={cls.home}>
      {/* MOBILE */}
      <section className="nu-mobile nu-bg--mobile">
        <div className={cls.stage}>
          <Motion.div
            className={`${cls.frame} ${cls["frame--primary"]}`}
            variants={appear}
            initial="hidden"
            animate="show"
          >
            <h1 className={`${cls.title} ${cls["is-mrs"]}`}>
              {t("resetPassword.title", {
                defaultValue: "Reset your password",
              })}
            </h1>
            <p className={cls.subtitle}>
              {t("resetPassword.subtitle", {
                defaultValue: "Enter a new password for your account.",
              })}
            </p>

            {err && (
              <div className={cls.errorText} role="alert">
                {err}
              </div>
            )}

            <ResetForm
              token={token}
              t={t}
              loading={loading}
              setLoading={setLoading}
              setErr={setErr}
            />
          </Motion.div>
        </div>
      </section>

      {/* DESKTOP */}
      <section className="nu-desktop nu-bg--desktop">
        <div className={cls.stage}>
          <Motion.div
            className={`${cls.frame} ${cls["frame--primary"]}`}
            variants={appear}
            initial="hidden"
            animate="show"
          >
            <h2
              className={`${cls.title} ${cls["is-mrs"]}`}
              style={{ marginBottom: 8 }}
            >
              {t("resetPassword.desktopTitle", {
                defaultValue: "Create a new password",
              })}
            </h2>
            <p className={cls.subtitle} style={{ marginBottom: 12 }}>
              {t("resetPassword.desktopHelp", {
                defaultValue: "Use the form to set a new password.",
              })}
            </p>

            {err && (
              <div className={cls.errorText} role="alert">
                {err}
              </div>
            )}

            {/* Reuse the same form on desktop */}
            <ResetForm
              token={token}
              t={t}
              loading={loading}
              setLoading={setLoading}
              setErr={setErr}
            />
          </Motion.div>
        </div>
      </section>
    </main>
  );
}
